"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  type AccountAddressFormData,
  getAccountAddress,
  updateAccountAddress,
} from "@/api/account-addresses"
import { Button } from "@/components/ui/button"
import { AddressFormFields } from "~/components/account/addresses/form"
import { AddressFormSkeleton } from "~/components/account/addresses/form/skeleton"

const EMPTY_FORM_VALUES: AccountAddressFormData = {
  email: "",
  firstName: "",
  lastName: "",
  cpf: "",
  phone: "",
  zipCode: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "SP",
}

export default function MinhaContaEnderecoEntregaPage() {
  const queryClient = useQueryClient()
  const form = useForm<AccountAddressFormData>({
    defaultValues: EMPTY_FORM_VALUES,
    mode: "onChange",
  })

  const addressQuery = useQuery({
    queryKey: ["account", "address", "shipping"],
    queryFn: () => getAccountAddress("shipping"),
    retry: false,
  })

  useEffect(() => {
    const address = addressQuery.data?.address

    if (addressQuery.data?.ok && address) {
      form.reset(address)
      return
    }

    if (addressQuery.data && !addressQuery.data.ok) {
      toast.error(
        addressQuery.data.message?.trim() ||
          "Nao foi possivel carregar o endereco da conta."
      )
    }
  }, [addressQuery.data, form])

  useEffect(() => {
    if (!addressQuery.isError) {
      return
    }

    toast.error("Nao foi possivel carregar o endereco da conta.")
  }, [addressQuery.isError])

  const updateAddressMutation = useMutation({
    mutationFn: updateAccountAddress,
    onMutate: async (variables) => {
      const toastId = toast.loading("Salvando endereco...")

      await queryClient.cancelQueries({
        queryKey: ["account", "address", "shipping"],
      })

      const previousAddress = queryClient.getQueryData([
        "account",
        "address",
        "shipping",
      ])

      queryClient.setQueryData(["account", "address", "shipping"], {
        ok: true,
        address: variables.address,
      })

      return { toastId, previousAddress }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAddress) {
        queryClient.setQueryData(
          ["account", "address", "shipping"],
          context.previousAddress
        )
      }

      toast.error("Nao foi possivel salvar o endereco.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        if (context?.previousAddress) {
          queryClient.setQueryData(
            ["account", "address", "shipping"],
            context.previousAddress
          )
        }

        toast.error(
          response.message?.trim() || "Nao foi possivel salvar o endereco.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      if (response.address) {
        form.reset(response.address)
      }

      toast.success("Endereco atualizado.", {
        id: context?.toastId,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["account", "address", "shipping"],
      })
    },
  })

  const onSubmit = (values: AccountAddressFormData) => {
    updateAddressMutation.mutate({
      mode: "shipping",
      address: values,
    })
  }

  const isFormDisabled =
    addressQuery.isPending || updateAddressMutation.isPending
  let submitLabel = "Salvar enderecos"

  if (addressQuery.isPending) {
    submitLabel = "Carregando..."
  } else if (updateAddressMutation.isPending) {
    submitLabel = "Salvando..."
  }

  // Show skeleton while initial data is loading
  if (addressQuery.isPending) {
    return <AddressFormSkeleton />
  }

  return (
    <section className='rounded-2xl border bg-zinc-100 p-6 shadow-xs md:p-8'>
      <div className='max-w-3xl space-y-6'>
        <h1 className='font-medium text-2xl'>Endereco de entrega</h1>

        <form className='space-y-3.5' onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset className='space-y-3.5' disabled={isFormDisabled}>
            <AddressFormFields mode='shipping' register={form.register} />

            <Button className='mt-6 h-12 px-8 text-base' type='submit'>
              {submitLabel}
            </Button>
          </fieldset>
        </form>
      </div>
    </section>
  )
}
